import { create } from "zustand";

import type { ParticipantCohortKind } from "../api/types";
import {
  decodeFilter,
  emptyFilter,
  encodeFilter,
  FILTER_STORAGE_KEY,
  type ParticipantFilter,
} from "../lib/tagfilter";

/**
 * Фильтр панели участников по РУЧНЫМ пометкам: кого оставить, кого убрать.
 *
 * # Почему у тега три состояния, а не два
 *
 * Потому что вопросов к пометке два, а не один: «покажи только этих» и «убери
 * этих». Второй не выражается через первый — «покажи всех, кроме разобранных»
 * потребовало бы выбрать все остальные теги поимённо, включая те, которых ещё
 * нет. Одно нажатие проводит тег по кольцу «не выбран → включён → исключён →
 * не выбран»: два независимых списка при одной кнопке на тег.
 *
 * # Почему стор, а не useState в панели
 *
 * По той же причине, что окно статистики и режим: рынки пятиминутные, и
 * локальное состояние сбрасывало бы фильтр при каждом переходе на новое окно —
 * ровно тогда, когда отобранная группа кошельков интереснее всего.
 *
 * # Почему ВСЁ-ТАКИ localStorage
 *
 * Решение оператора (2026-09-02), и оно изменило прежнее. Довод против был
 * такой: фильтр СУЖАЕТ таблицу, и сохранённый между сессиями он встретит
 * оператора наполовину пустой панелью — забытый фильтр это худший вид вранья на
 * терминале, где данные на экране верны, а картина мира нет.
 *
 * Довод перевесила цена набора: отбор «плюсовые тейкеры, но без разобранных» —
 * это шесть нажатий, а вкладку терминала перезагружают по десять раз за смену,
 * и каждый раз он собирался заново. Соседние сторы (окно статистики, режим,
 * сортировка) не сохраняются по другой причине — у них умолчание задаёт сервер,
 * — и на этот перенести её нельзя: умолчания фильтра сервер не знает.
 *
 * Вранья при этом не осталось, потому что забыть фильтр негде: над таблицей
 * стои́т жёлтая строка со знаменателем («2 кошелька из 856 в окне»), на кнопке
 * «теги» — счётчик нажатого, рядом с ней — крестик, снимающий всё одним
 * движением. Восстанавливается фильтр молча и целиком; любая порча хранилища
 * даёт ПУСТОЙ фильтр, то есть полный рынок (см. decodeFilter).
 */
export type TagState = "off" | "include" | "exclude";

interface ParticipantTagsStore {
  /** Кого ОСТАВИТЬ. Несколько тегов читаются как ИЛИ — см. серверный keep. */
  include: string[];
  /** Кого УБРАТЬ. Сильнее включения: спор решается в пользу «убрать». */
  exclude: string[];
  /**
   * То же самое по ПОДГРУППЕ сводки: «покажи только плюсовых тейкеров».
   *
   * Отдельные списки, а не общие с ручными пометками, потому что величины
   * разные: пометку произнёс человек, подгруппу вывел обход — и уезжают они в
   * разных параметрах запроса. Один стор на оба, потому что фильтр на экране
   * один: один счётчик нажатого, один крестик «снять всё».
   *
   * Пересекаются как И: «мои киты» и «плюсовые тейкеры» вместе означают «мои
   * киты, которые плюсовые тейкеры» — так это и считает сервер.
   */
  groupInclude: ParticipantCohortKind[];
  groupExclude: ParticipantCohortKind[];
  /** Состояние одного тега — то, чем красится кнопка. */
  state: (tag: string) => TagState;
  /**
   * Поставить тегу состояние ПРЯМО — вход двух кнопок списка фильтра.
   *
   * Кольцо (`cycle`) осталось для чипа в строке кошелька, где кнопка одна и
   * места на две нет. В самом же списке фильтра оба вопроса названы словами и
   * стоят одного нажатия каждый: кольцо нигде не написано на экране, и второе
   * действие приходилось находить перебором.
   */
  set: (tag: string, state: TagState) => void;
  /** Кольцо состояний по нажатию. */
  cycle: (tag: string) => void;
  /** Состояние подгруппы — то же самое для системных чипов. */
  groupState: (kind: ParticipantCohortKind) => TagState;
  setGroup: (kind: ParticipantCohortKind, state: TagState) => void;
  cycleGroup: (kind: ParticipantCohortKind) => void;
  /** Снять фильтр целиком — оба вида пометок разом. */
  clear: () => void;
  /** Нажато ли хоть что-нибудь. Счётчик на кнопке фильтра. */
  active: () => number;
}

/**
 * Чтение хранилища — один раз на загрузку модуля.
 *
 * Отказ хранилища (политика «блокировать всё», приватное окно) роняет не
 * вкладку, а фильтр: пустой фильтр — это полный рынок, то есть безопасное
 * состояние. Молчать здесь можно ровно потому, что фильтр виден на экране
 * всегда; настройки размеров в той же ситуации кричат (см. useSettings.save),
 * и разница не в лени, а в цене: там молчание стоило бы ордера неверного
 * размера.
 */
function load(): ParticipantFilter {
  try {
    return decodeFilter(localStorage.getItem(FILTER_STORAGE_KEY));
  } catch {
    return emptyFilter();
  }
}

function save(f: ParticipantFilter): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, encodeFilter(f));
  } catch {
    // См. load(): потеря фильтра между сессиями безопасна и видна на экране.
  }
}

const initial = load();

export const useParticipantTags = create<ParticipantTagsStore>((set, get) => ({
  include: initial.include,
  exclude: initial.exclude,
  groupInclude: initial.groupInclude,
  groupExclude: initial.groupExclude,
  state: (tag) => {
    const s = get();
    if (s.include.includes(tag)) return "include";
    if (s.exclude.includes(tag)) return "exclude";
    return "off";
  },
  set: (tag, state) =>
    // Тег снимается из ОБОИХ списков и добавляется в один: состояния взаимно
    // исключают друг друга, и «показать» поверх «убрать» обязано отменить
    // прежнее, а не оставить кошелёк в двух ответах сразу.
    set((s) => ({
      include:
        state === "include"
          ? [...s.include.filter((t) => t !== tag), tag]
          : s.include.filter((t) => t !== tag),
      exclude:
        state === "exclude"
          ? [...s.exclude.filter((t) => t !== tag), tag]
          : s.exclude.filter((t) => t !== tag),
    })),
  cycle: (tag) =>
    set((s) => {
      if (s.include.includes(tag)) {
        // Включён → исключён: тег переезжает из списка в список, а не снимается.
        // Иначе «показать только их» и «убрать их» требовали бы двух разных
        // кнопок на один и тот же тег.
        return {
          include: s.include.filter((t) => t !== tag),
          exclude: [...s.exclude, tag],
        };
      }
      if (s.exclude.includes(tag)) {
        return { exclude: s.exclude.filter((t) => t !== tag) };
      }
      return { include: [...s.include, tag] };
    }),
  groupState: (kind) => {
    const s = get();
    if (s.groupInclude.includes(kind)) return "include";
    if (s.groupExclude.includes(kind)) return "exclude";
    return "off";
  },
  // Те же два действия и то же кольцо, что у ручных пометок, — на своих списках.
  // Повторять правила разбора состояний в разметке нельзя: два места, где
  // решается «что делает нажатие», разъедутся первым же исправлением.
  setGroup: (kind, state) =>
    set((s) => ({
      groupInclude:
        state === "include"
          ? [...s.groupInclude.filter((k) => k !== kind), kind]
          : s.groupInclude.filter((k) => k !== kind),
      groupExclude:
        state === "exclude"
          ? [...s.groupExclude.filter((k) => k !== kind), kind]
          : s.groupExclude.filter((k) => k !== kind),
    })),
  cycleGroup: (kind) => {
    const next = get().groupState(kind);
    get().setGroup(
      kind,
      next === "off" ? "include" : next === "include" ? "exclude" : "off",
    );
  },
  clear: () =>
    set({ include: [], exclude: [], groupInclude: [], groupExclude: [] }),
  active: () => {
    const s = get();
    return (
      s.include.length +
      s.exclude.length +
      s.groupInclude.length +
      s.groupExclude.length
    );
  },
}));

/**
 * Запись — ПОДПИСКОЙ, а не строкой в каждом действии.
 *
 * Действий у фильтра шесть (два кольца, две прямые установки, снятие), и
 * дописывать сохранение в каждое значило бы завести шесть мест, где его можно
 * забыть, — а забытое сохранение выглядит как «фильтр не сохраняется иногда».
 * Подписка ловит любое изменение состояния по построению.
 */
useParticipantTags.subscribe((s) => {
  save({
    include: s.include,
    exclude: s.exclude,
    groupInclude: s.groupInclude,
    groupExclude: s.groupExclude,
  });
});
